import { DateTime, Duration } from "luxon"
import { getMessageChannelText } from "./Types/TypeUtils"
import { gql } from "@apollo/client"
import ImageWithLoader from "./ImageWithLoader"
export const DateTimeFormat = "MM/dd/yyyy h:mm a"
export const DateFormat = "MM/dd/yyyy"
export const TimeFormat = "hh:mm a"

export function showError(toast, mge, error) {
    if (error) {
        console.log(`${mge}: ${error}`)
    }
    toast.current.show({
        severity: "error",
        detail: mge,
        life: 3000,
    })
}

export const formatDate = (date, format) => {
    return DateTime.fromISO(date).toFormat(format)
}

export const getTimeAgo = (date) => {
    if (date != null && date !== "0001-01-01T00:00:00Z") {
        return DateTime.fromISO(date, { zone: 'utc' }).toLocal().toRelative()
    }
    return "-"
}

export const getActualUTCTime = () => {
    return DateTime.utc().toISO()
}

export const convertToLocalDateTime = (date) =>
    DateTime.fromISO(date, { zone: "utc" }).toLocal().toJSDate()

export const formatToLocalDateTime = (date) =>
    DateTime.fromISO(date, { zone: "utc" }).toLocal().toFormat(DateTimeFormat)

export const sortListByDate = (list, dateField) => {
    list.sort((a, b) => {
        let dateA = DateTime.fromISO(a[dateField])
        let dateB = DateTime.fromISO(b[dateField])
        return dateB - dateA
    })
}

export const groupMessagesByDate = (messages, fromIdFieldName) => {
    let groupedMessages = []
    let lastFromId = null
    let lastKey = null
    let lastGroupIndex = -1

    messages.forEach(message => {
        const relativeTime = DateTime.fromISO(message.dateUpdated).toRelative()
        const key = `${relativeTime}_${message[fromIdFieldName]}`
        if (key === lastKey && lastFromId === message[fromIdFieldName]) {
            groupedMessages[lastGroupIndex].messages.push(message)
        } else {
            groupedMessages.push({
                key: key,
                messages: [message]
            })
            lastGroupIndex++
        }
        lastFromId = message[fromIdFieldName]
        lastKey = key
    })
    return groupedMessages
}

export const truncateString = (string, limit) => {
    if (string.length > limit) {
        return string.substring(0, limit) + "..."
    } else {
        return string
    }
}

export const formatChannels = (channels) => {
    if (channels) {
        return channels.map(channel => getMessageChannelText(channel)).join(", ")
    }
}

const displayBody = (body) => {
    //if body contains \r\n means it's a multiline message and should show it with line breaks
    if (body.includes("\r\n")) {
        return body.split("\r\n").map((line, i) => (
            <p key={i}>{line}</p>
        ))
    } else {
        return <p>{body}</p>
    }
}

export const transformMessage = (message) => {
    //if mge contains a twilio or gcp bucket link means it's an image and should show it as image
    let regex = ""
    if (message.body.includes("https://p.twil.io")) {
        regex = /https:\/\/p\.twil\.io\/\S+/g
    } else if (message.body.includes("https://storage.googleapis.com")) {
        regex = /https:\/\/storage\.googleapis\.com\/\S+/g
    }
    if (regex !== "") {
        let links = message.body.match(regex)
        let textWithoutLinks = message.body.replace(regex, '').trim()
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                {displayBody(textWithoutLinks)}
                {links.map((link, i) => (
                    <ImageWithLoader aKey={i} src={link} width="150" />
                ))}
            </div>
        )
    } else {
        if (message.mediaUrl) {
            return (
                <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    {displayBody(message.body)}
                    <ImageWithLoader src={message.mediaUrl} width="150" />
                </div>
            )
        }
        return displayBody(message.body)
    }
}

export const formatPhoneNumber = (string) => {
    //String is with this format: +12015354255
    //Format to: (201) 535-4255
    if (!string) {
        return ""
    }
    let cleaned = ("" + string).replace(/\D/g, "")
    //Remove 1 from the beginning
    if (cleaned.length === 11 && cleaned[0] === "1") {
        cleaned = cleaned.slice(1)
    }
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3]
    }
    return ""
}

export const cleanPhoneNumber = (formattedPhone) => {
    let cleaned = formattedPhone.replace(/\D/g, '')
    if (cleaned.length === 11 && cleaned[0] === "1") {
        cleaned = cleaned.slice(1)
    }
    return cleaned
}

export const getRejectedReasonText = (reason) => {
    if (reason === 'ParticipantNotInOrg')
        return 'Participant Not In Organization'
    if (reason === 'ParticipantInactive')
        return 'Inactive Participant'
}

export const findName = (id, list) => {
    const staff = list.find((staff) => staff.staff_id === id)
    return staff ? { first: staff.staff_name_first, last: staff.staff_name_last, full_name: staff.staff_full_name } : {
        first: "", last: "", full_name: ""
    }
}
export const formatDuration = (durationInSec) => {
    // Create a Duration object from seconds
    const duration = Duration.fromObject({ seconds: durationInSec })
    // Format the duration to HH:mm:ss
    return duration.toFormat('hh:mm:ss')
}

//Get device type icon based on the device type string
export const getDeviceTypeIcon = (deviceType) => {
    switch (deviceType ? deviceType.toLowerCase() : deviceType) {
        case "android":
            return "pi pi-android"
        case "ios":
            return "pi pi-apple"
        case "web":
        case "windows":
        case "mac":
            return "pi pi-desktop"
        case "mobile":
            return "pi pi-mobile"
        default:
            return "pi pi-video"
    }
}

export const atLeastOneChannelSelected = (selectedChannels) => {
    return Object.values(selectedChannels).some(value => value === true)
}

export const requiredFieldIsMissing = (field, fieldType) => {
    if (field === null || field === undefined) return true
    if (fieldType && fieldType === "bool") return false
    if (
        (typeof field === "string" || field instanceof String) &&
        field.trim() === ""
    )
        return true
    return false
}

export const renderRequireFieldErrorMessage = (field, mge) => {
    if (requiredFieldIsMissing(field))
        return <small className="p-error" style={{ display: "block" }}>{mge}</small>
    return null
}

export const camelToFlat = (camel) => {
    return camel.replace(/([a-z])([A-Z])/g, '$1 $2')
}

export const deepClone = (obj) => {
    return JSON.parse(JSON.stringify(obj))
}

export const isEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) return false

    let keys1 = Object.keys(obj1)
    let keys2 = Object.keys(obj2)

    if (keys1.length !== keys2.length) return false

    for (let key of keys1) {
        if (!keys2.includes(key) || !isEqual(obj1[key], obj2[key])) {
            return false
        }
    }

    return true
}

export const stringToBool = (str) => {
    if (str === null || str === undefined || str === '') return false
    return JSON.parse(str.toLowerCase())
}

export const formatPhoneNumberInput = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '')
    let formattedNumber = ''

    if (cleaned.length <= 3) {
        formattedNumber = `(${cleaned}`
    } else if (cleaned.length <= 6) {
        formattedNumber = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}`
    } else {
        formattedNumber = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`
    }

    return formattedNumber
}

export const validatePhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber?.replace(/\D/g, '')
    return cleaned?.length === 10
}

export const capitalizeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
}

export const GET_ORGANIZATIONS = gql`
query getOrganizations($staff_id: ID!, $enterprise_id: ID!) {
    organizations: organizationsByEnterpriseUid(
        staff_id: $staff_id,  
        enterprise_id: $enterprise_id
        ) {
            id: organization_id
            name: organization_name
        }
}
`

export const GET_STAFF_NAMES = gql`
  query getStaffNamesByIds($staff_id: ID!, $organization_id: ID!, $staff_ids: [ID!]!) {
    getStaffNamesByIds(staff_id:$staff_id, organization_id:$organization_id, staff_ids: $staff_ids) {
        data
    }
  }
`